// requestKey.js — BL-048 (ADR-068) client request-idempotency key management.
//
// A `request_key` (UUID) binds one logical Analyze action. The client:
//   • mints a key when a submit begins,
//   • REUSES the same key for a retry of the SAME unchanged request (so a
//     network retry dedups server-side — no double credit/analysis), and
//   • REGENERATES the key whenever an accepted request field changes (including
//     lead_id) or on a new deliberate Analyze after a 409.
//
// On a 409 `idempotency_key_reused` the UI STOPS, shows a bounded message, does
// NOT auto-resubmit, preserves form state, and mints a new key only when the
// user explicitly submits again. All logic here is pure + unit-tested.

// Accepted-field client fingerprint — mirrors the server contract's registry
// field names (analysisRequestContract) so "an accepted field changed" is
// detected client-side. Kept in sync with the server registry.
export const ACCEPTED_FIELDS = [
  "purchasePrice", "loanAmount", "interestRate", "termYears", "expenseRatio",
  "insuranceMonthly", "utilitiesMonthly", "maintenanceRatio", "daysPerMonth",
  "current_monthly_revenue", "latitude", "longitude", "formatted_address",
  "address", "street", "city", "state", "zip", "place_id", "runSimulation",
  "forecastMonths", "entry_type", "strScenarioMonthlyNoi", "lead_id",
];

export const KEY_REUSED_MESSAGE =
  "This request changed since it was started — press Analyze to run it again.";

export function newRequestKey() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  // Deterministic-enough fallback (non-crypto envs / older jsdom).
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

/**
 * Stable client fingerprint of the accepted fields only (order-independent).
 */
export function clientFingerprint(payload = {}) {
  const picked = {};
  for (const f of ACCEPTED_FIELDS) {
    const v = payload?.[f];
    picked[f] = v === undefined ? null : v;
  }
  return JSON.stringify(picked, Object.keys(picked).sort());
}

/**
 * resolveRequestKey(prev, payload, { forceNew }) → { key, fingerprint, changed }
 *
 * `prev` = { key, fingerprint } from the last submit (or null/undefined).
 * Reuses the key when the accepted-field fingerprint is unchanged and forceNew
 * is false; otherwise mints a new key.
 */
export function resolveRequestKey(prev, payload, { forceNew = false } = {}) {
  const fingerprint = clientFingerprint(payload);
  if (forceNew || !prev || !prev.key || prev.fingerprint !== fingerprint) {
    return { key: newRequestKey(), fingerprint, changed: true };
  }
  return { key: prev.key, fingerprint, changed: false };
}

/**
 * Detect the idempotency-conflict response (before-compute 409).
 */
export function isIdempotencyConflict(response, data) {
  return (response && response.status === 409) || data?.code === "idempotency_key_reused";
}
